export type ElevationPoint = {
  lat: number;
  lng: number;
  elevationMeters: number;
  elevationFeet: number;
};

export type ElevationDeltaResult = {
  from: ElevationPoint;
  to: ElevationPoint;
  deltaFeet: number;
};

const FETCH_TIMEOUT_MS = 8000;

// Recalculating a shot (re-checking club choice, re-reading wind, etc.)
// re-requests the same tee/target pair repeatedly. Each call hits a
// rate-limited upstream API (OpenTopoData, 1 req/sec) through a Netlify
// function, so a repeat within a short window is the main avoidable source
// of "significant latency" — cache it instead of refetching.
const CACHE_TTL_MS = 5 * 60_000;
const elevationCache = new Map<string, { result: ElevationDeltaResult; expiresAt: number }>();

function cacheKey(args: { fromLat: number; fromLng: number; toLat: number; toLng: number }): string {
  // ~1m precision — plenty for a coordinate pair that's meant to represent
  // the same tee/target, while still treating a real move as a cache miss.
  const r = (n: number) => n.toFixed(5);
  return `${r(args.fromLat)},${r(args.fromLng)}->${r(args.toLat)},${r(args.toLng)}`;
}

async function fetchJson<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(url, { signal: controller.signal });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new Error(`Elevation request timed out after ${FETCH_TIMEOUT_MS / 1000}s — check your connection`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`Elevation request failed: ${res.status} ${text}`);
  }

  return JSON.parse(text) as T;
}

export async function getElevationDeltaFt(args: {
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
}): Promise<ElevationDeltaResult> {
  const key = cacheKey(args);
  const cached = elevationCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.result;
  }

  const url =
    `/.netlify/functions/elevation` +
    `?fromLat=${encodeURIComponent(String(args.fromLat))}` +
    `&fromLng=${encodeURIComponent(String(args.fromLng))}` +
    `&toLat=${encodeURIComponent(String(args.toLat))}` +
    `&toLng=${encodeURIComponent(String(args.toLng))}`;

  const result = await fetchJson<ElevationDeltaResult>(url);
  elevationCache.set(key, { result, expiresAt: Date.now() + CACHE_TTL_MS });
  return result;
}